/**
 * Session key lifecycle for Fume forward-secrecy erasure (S7 / invariant I2).
 *
 * The lifecycle owns the JS-visible secret buffers for one session and provides
 * a single erasure point. TTL is only a local key-lifetime signal: it erases the
 * managed local key buffers on access after expiry, but it does not and cannot
 * delete fragments or other wire artifacts buffered in carriers, mailboxes,
 * TURN relays, caches, backups, or logs.
 */
import { FumeCryptoError } from './errors.js';
import { deriveKeys } from './key-schedule.js';
import { DH_SECRET_SIZE, SESSION_ROOT_SIZE, X25519_PUBLIC_KEY_SIZE } from './constants.js';
import { requireLength } from './internal.js';
import { x25519SharedSecret } from './x25519.js';
const X25519_BASE_POINT = new Uint8Array(32);
X25519_BASE_POINT[0] = 9;
/** Error thrown for all lifecycle misuse. */
export class LifecycleError extends FumeCryptoError {
    constructor(message) {
        super(message);
        this.name = 'LifecycleError';
    }
}
export class SessionKeyLifecycle {
    ttlSecondsValue;
    createdAtValue;
    ephemeralPublicKeyValue;
    ephemeralSecretKeyValue;
    dhValue;
    contentKeyValue;
    manifestKeyValue;
    receiptKeyValue;
    destroyed = false;
    constructor(options) {
        let resolved;
        try {
            validateOptions(options);
            resolved = resolveKeySource(options);
            const keys = deriveKeys(options.R, resolved.dh, options.transcript);
            this.ttlSecondsValue = options.ttlSeconds;
            this.createdAtValue = options.createdAt;
            this.ephemeralPublicKeyValue = resolved.ephemeralPublicKey;
            this.ephemeralSecretKeyValue = resolved.ephemeralSecretKey;
            this.dhValue = resolved.dh;
            this.contentKeyValue = keys.contentKey;
            this.manifestKeyValue = keys.manifestKey;
            this.receiptKeyValue = keys.receiptKey;
        }
        catch (err) {
            if (resolved?.ownsDh === true)
                zeroize(resolved.dh);
            if (err instanceof LifecycleError)
                throw err;
            if (err instanceof Error)
                throw new LifecycleError(err.message);
            throw new LifecycleError('session key lifecycle construction failed');
        }
    }
    /** Ephemeral X25519 public key (non-secret). Available if the secret key was supplied. */
    get ephemeralPublicKey() {
        if (this.ephemeralPublicKeyValue === undefined) {
            throw new LifecycleError('ephemeral public key is unavailable');
        }
        return this.ephemeralPublicKeyValue;
    }
    /** True after destroy() has run. */
    get isDestroyed() {
        return this.destroyed;
    }
    /**
     * TTL is a local key-lifetime signal only. Expiry can erase this object's
     * local secret buffers, but it does not delete buffered wire artifacts.
     */
    isExpired(now) {
        assertNonNegativeInteger(now, 'now');
        return now >= this.createdAtValue + this.ttlSecondsValue;
    }
    /**
     * Return the live managed content-key buffer.
     *
     * ACCESS MODEL (reference model): accessors return the exact internal
     * Uint8Array. On destroy() that buffer is zero-filled, so every previously
     * obtained reference becomes all-zero. The object retains no second copy.
     */
    getContentKey(now) {
        return this.getLiveKey(now, this.contentKeyValue, 'content key');
    }
    /** Same contract as getContentKey. */
    getManifestKey(now) {
        return this.getLiveKey(now, this.manifestKeyValue, 'manifest key');
    }
    /** Same contract as getContentKey. */
    getReceiptKey(now) {
        return this.getLiveKey(now, this.receiptKeyValue, 'receipt key');
    }
    /**
     * Single-point erasure. Zero-fills every secret Uint8Array held by this
     * object and is idempotent. Public/non-secret data is left intact.
     */
    destroy() {
        if (this.destroyed)
            return;
        zeroize(this.ephemeralSecretKeyValue);
        zeroize(this.dhValue);
        zeroize(this.contentKeyValue);
        zeroize(this.manifestKeyValue);
        zeroize(this.receiptKeyValue);
        this.destroyed = true;
    }
    /** Honest FS capability descriptor for this JS/WASM build. */
    getForwardSecrecyTier() {
        return {
            tier: 'best-effort',
            mlock: false,
            coreDumpsDisabled: false,
            encryptedSwapAssumed: false,
            notes: 'JS/WASM runtime: fill(0) zeroizes JS-visible buffers only. ' +
                'No mlock, no core-dump control, no guarantee against GC copies, ' +
                'swap, hibernation, or WASM linear-memory copies. ' +
                'Use native build for strong FS.',
        };
    }
    getLiveKey(now, key, name) {
        if (this.destroyed) {
            throw new LifecycleError(`${name} is destroyed`);
        }
        if (this.isExpired(now)) {
            this.destroy();
            throw new LifecycleError(`${name} is expired`);
        }
        return key;
    }
}
export function createSessionKeyLifecycle(options) {
    return new SessionKeyLifecycle(options);
}
function validateOptions(options) {
    if (options == null || typeof options !== 'object') {
        throw new LifecycleError('options are required');
    }
    requireLength(options.R, SESSION_ROOT_SIZE, 'R');
    validateTranscript(options.transcript);
    assertNonNegativeInteger(options.ttlSeconds, 'ttlSeconds');
    assertNonNegativeInteger(options.createdAt, 'createdAt');
}
function validateTranscript(transcript) {
    if (transcript == null ||
        typeof transcript !== 'object' ||
        !(transcript.hash instanceof Uint8Array) ||
        transcript.hash.length !== SESSION_ROOT_SIZE) {
        throw new LifecycleError(`transcript.hash must be exactly ${SESSION_ROOT_SIZE} bytes`);
    }
    if (typeof transcript.sasVerified !== 'boolean') {
        throw new LifecycleError('transcript.sasVerified must be a boolean');
    }
}
function assertNonNegativeInteger(value, name) {
    if (!Number.isInteger(value) || value < 0) {
        throw new LifecycleError(`${name} must be a non-negative integer`);
    }
}
function resolveKeySource(options) {
    const hasDh = options.dh !== undefined;
    const hasKeyPair = options.ephemeralKeyPair !== undefined;
    const hasSecretKey = options.ephemeralSecretKey !== undefined;
    const hasPeerPublicKey = options.peerPublicKey !== undefined;
    if (hasDh) {
        if (hasKeyPair || hasPeerPublicKey) {
            throw new LifecycleError('dh key source must not also provide ephemeralKeyPair or peerPublicKey');
        }
        requireLength(options.dh, DH_SECRET_SIZE, 'dh');
        if (hasSecretKey) {
            requireLength(options.ephemeralSecretKey, DH_SECRET_SIZE, 'ephemeralSecretKey');
            return {
                dh: options.dh,
                ephemeralSecretKey: options.ephemeralSecretKey,
                ephemeralPublicKey: x25519SharedSecret(options.ephemeralSecretKey, X25519_BASE_POINT),
                ownsDh: false,
            };
        }
        return { dh: options.dh, ownsDh: false };
    }
    if (hasKeyPair) {
        if (hasSecretKey) {
            throw new LifecycleError('provide either ephemeralKeyPair or ephemeralSecretKey, not both');
        }
        if (!hasPeerPublicKey) {
            throw new LifecycleError('peerPublicKey is required with ephemeralKeyPair');
        }
        requireLength(options.ephemeralKeyPair.secretKey, DH_SECRET_SIZE, 'ephemeralKeyPair.secretKey');
        requireLength(options.ephemeralKeyPair.publicKey, X25519_PUBLIC_KEY_SIZE, 'ephemeralKeyPair.publicKey');
        const dh = x25519SharedSecret(options.ephemeralKeyPair.secretKey, options.peerPublicKey);
        return {
            dh,
            ephemeralSecretKey: options.ephemeralKeyPair.secretKey,
            ephemeralPublicKey: options.ephemeralKeyPair.publicKey,
            ownsDh: true,
        };
    }
    if (hasSecretKey) {
        if (!hasPeerPublicKey) {
            throw new LifecycleError('peerPublicKey is required with ephemeralSecretKey');
        }
        requireLength(options.ephemeralSecretKey, DH_SECRET_SIZE, 'ephemeralSecretKey');
        const dh = x25519SharedSecret(options.ephemeralSecretKey, options.peerPublicKey);
        return {
            dh,
            ephemeralSecretKey: options.ephemeralSecretKey,
            ephemeralPublicKey: x25519SharedSecret(options.ephemeralSecretKey, X25519_BASE_POINT),
            ownsDh: true,
        };
    }
    throw new LifecycleError('one complete key source is required');
}
function zeroize(buf) {
    if (buf !== undefined)
        buf.fill(0);
}
//# sourceMappingURL=lifecycle.js.map